import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDispatch, useSelector } from "react-redux";
import AttendanceService from "../../service/AttendanceService";
import { fetchEmployeeAttendance } from "../../action/EmployeeAttendanceAction";
import dayjs from "dayjs";

// ─── Color helpers ───────────────────────────────────────────────────────────
const rateColor = (pct) =>
  pct >= 80 ? "#16a34a" : pct >= 60 ? "#d97706" : "#dc2626";

const rateBg = (pct) =>
  pct >= 80 ? "#dcfce7" : pct >= 60 ? "#fef3c7" : "#fee2e2";

// ─── Styled shells ───────────────────────────────────────────────────────────
const Page = styled(Box)(({ theme }) => ({
  width: "100%",
}));

const Card = styled(Box)({
  background: "#fff",
  border: "0.5px solid #e2e8f0",
  borderRadius: 12,
  overflow: "hidden",
});

const SectionLabel = styled(Typography)({
  fontSize: 11,
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.6px",
  marginBottom: 6,
});

function PctBadge({ pct }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: "7px",
        py: "1px",
        borderRadius: "10px",
        fontSize: 11,
        fontWeight: 600,
        background: rateBg(pct),
        color: rateColor(pct),
      }}
    >
      {pct}%
    </Box>
  );
}

export default function EmployeeAttendanceChart() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();
  const { loading, data } = useSelector(
    (s) => s.employeeAttendance
  );

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [divisions, setDivisions] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState("DPM");

  const formatDate = (d) => dayjs(d).format("YYYY-MM-DD");

  const displayDivisions = useMemo(() => {
    const list = [...divisions];

    if (!list.some((d) => d.DivisionCode === "DPM")) {
      list.push({
        DivisionCode: "DPM",
        DivisionName: "Project and People Management",
      });
    }

    return list;
  }, [divisions]);

  useEffect(() => {
    AttendanceService.GetAllDivisions()
      .then((res) =>
        setDivisions(res?.data?.ResultSet || res?.data || [])
      )
      .catch(console.error);
  }, []);

  useEffect(() => {
    dispatch(
      fetchEmployeeAttendance(
        formatDate(selectedDate),
        selectedDivision
      )
    );
  }, [dispatch, selectedDivision]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);

    dispatch(
      fetchEmployeeAttendance(
        formatDate(date),
        selectedDivision
      )
    );
  };

  const handleLocationClick = (location) => {
  navigate("/DTSLocAtt", {
    state: {
      location,
      date: formatDate(selectedDate),
      division: selectedDivision,
    },
  });
};

  const handleDivisionChange = (e) => {
    const div = e.target.value;

    setSelectedDivision(div);

    dispatch(
      fetchEmployeeAttendance(
        formatDate(selectedDate),
        div
      )
    );
  };

  const sortedData = useMemo(
    () =>
      data
        ? [...data].sort((a, b) =>
            a.Emp_location.localeCompare(b.Emp_location)
          )
        : [],
    [data]
  );

  const chartData = useMemo(
    () =>
      sortedData
        .filter((item) => Number(item.Emp_real || 0) > 0)
        .map((item) => ({
          location: item.Emp_location,
          total: Number(item.Emp_Total),
          real: Number(item.Emp_real),
          percentage: Math.round(Number(item.Percentage)),
        })),
    [sortedData]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
<Page>
  <Paper
    elevation={0}
    sx={{
      overflow: "hidden",
      backgroundColor: "#F4F6FA",
      borderRadius: "20px",
      padding: { xs: "16px", sm: "20px", md: "24px" },
      border: "1px solid #D0D8EC",
      boxShadow: "0 4px 24px rgba(37,99,235,0.07)",
    }}
  >
    {/* Header */}
    <Box sx={{ mb: 3 }}>
      <Typography
        sx={{
          fontSize: { xs: "17px", sm: "19px", md: "21px" },
          fontWeight: 700,
          color: "#330066",
          letterSpacing: "-0.01em",
          mb: 1,
        }}
      >
        Employee Attendance Overview
      </Typography>

      <Typography
        sx={{
          fontSize: 13,
          color: "#64748B",
        }}
      >
        Attendance percentage by location
      </Typography>
    </Box>
        {/* Filters */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 1.5,
            alignItems: "center",
          }}
        >
          <FormControl
            sx={{ flex: 1, minWidth: 0 }}
            size="small"
          >
            <Select
              value={selectedDivision}
              onChange={handleDivisionChange}
              sx={{
                height: 36,
                borderRadius: "20px",
                background: "#fff",
                fontSize: 12,
                fontWeight: 500,
                color: "#1e293b",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#e2e8f0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#330066",
                },
                "& .MuiSelect-select": {
                  py: 0,
                },
              }}
            >
              {displayDivisions.map((d) => (
                <MenuItem
                  key={d.DivisionCode}
                  value={d.DivisionCode}
                >
                  {d.DivisionCode} – {d.DivisionName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            onClick={() => setOpenDatePicker(true)}
            sx={{
              height: 36,
              borderRadius: "20px",
              border: "0.5px solid #e2e8f0",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              px: "12px",
              cursor: "pointer",
            }}
          >
            <CalendarTodayIcon
              sx={{ fontSize: 14, color: "#64748b" }}
            />

            <Typography
              sx={{
                fontSize: 12,
                color: "#1e293b",
                fontWeight: 500,
              }}
            >
              {formatDate(selectedDate)}
            </Typography>
          </Box>

          {openDatePicker && (
            <DatePicker
              open={openDatePicker}
              value={selectedDate}
              onChange={handleDateChange}
              onClose={() => setOpenDatePicker(false)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ display: "none" }}
                />
              )}
            />
          )}
        </Box>

        {/* Chart */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 8,
            }}
          >
            <CircularProgress
              size={28}
              sx={{ color: "#330066" }}
            />
          </Box>
        ) : chartData.length > 0 ? (
          <>
            <SectionLabel>
  Attendance by Location ({chartData.length} locations)
</SectionLabel>

<Box
  sx={{
    mt: 1,
    p: 2,
    borderRadius: "14px",
    background: "#fff",
    border: "1px solid #E2E8F0",
  }}
>
              {chartData.map((row) => {
                const barW = Math.round(
                  (row.real /
                    Math.max(
                      ...chartData.map((r) => r.total)
                    )) *
                    100
                );

                return (
                  <Box
                    key={row.location}
                    onClick={() => handleLocationClick(row)}
                    sx={{
                      mb: "10px",
                      cursor: "pointer",
                      "&:last-child": {
                        mb: 0,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: "3px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <LocationOnIcon
                          sx={{
                            fontSize: 12,
                            color: "#330066",
                          }}
                        />

                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#1e293b",
                          }}
                        >
                          {row.location}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            color: "#64748b",
                          }}
                        >
                          {row.real}/{row.total}
                        </Typography>

                        <PctBadge pct={row.percentage} />
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        height: 5,
                        background: "#f1f5f9",
                        borderRadius: "10px",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          borderRadius: "10px",
                          width: `${barW}%`,
                          background: rateColor(
                            row.percentage
                          ),
                          transition: "width 0.5s ease",
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "center",
                  pt: "10px",
                  mt: "8px",
                  borderTop: "0.5px solid #f1f5f9",
                }}
              >
                {[
                  {
                    label: "≥80%",
                    color: "#16a34a",
                  },
                  {
                    label: "60–79%",
                    color: "#92400e",
                  },
                  {
                    label: "<60%",
                    color: "#b91c1c",
                  },
                ].map((l) => (
                  <Box
                    key={l.label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "2px",
                        background: l.color,
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "#64748b",
                      }}
                    >
                      {l.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
          </Box>
          </>
        ) : (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography
              sx={{
                fontSize: 14,
                color: "#94a3b8",
              }}
            >
              No attendance data found
            </Typography>
          </Box>
        )}
       </Paper>
    </Page>
    </LocalizationProvider>
  );
}